import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  CircleAlert,
  Download,
  Play,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  cn,
} from '@/components/ui';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { getUnitsLabel, isUnitsSpecified } from '@/components/common/units';
import { PLACEHOLDER } from '@/logic/statistics';
import { useAppStore } from '@/store/appStore';
import { selectIsProcessing, useProgressStore } from '@/store/progressStore';
import { planTransform } from '@/utils/transformSelection';

interface ExportPanelProps {
  /** Re-solve with the current inputs; keeps the user on Export. */
  onResolve: () => void;
  /** Download the current transformed coordinates as PNEZD CSV. */
  onDownloadCsv: () => void;
  onGoToAdjust: () => void;
}

const SAMPLE_NAMES = 6;

// First and last columns line up with the card's content padding.
const EDGE_START = 'pl-4 sm:pl-5';
const EDGE_END = 'pr-4 sm:pr-5';

const timeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const GroupHeading = ({
  dot,
  label,
  detail,
}: {
  dot: string;
  label: string;
  detail: string;
}) => (
  <span className="inline-flex items-center gap-1.5">
    <span aria-hidden="true" className={cn('size-2 rounded-full', dot)} />
    <span className="text-fg">{label}</span>
    <span className="normal-case tracking-normal text-fg-muted">{detail}</span>
  </span>
);

/**
 * Export step: output options, the transform action, a preview of the
 * transformed coordinates with per-point radial σ, and the CSV download.
 * Final coordinates require a current solution and current output.
 */
export const ExportPanel = ({
  onResolve,
  onDownloadCsv,
  onGoToAdjust,
}: ExportPanelProps) => {
  const {
    units,
    solution,
    solutionStale,
    solutionRevision,
    transformedPoints,
    transformedStale,
    transformedTimestamp,
    transformedFromRevision,
    system1Points,
    pointNumberOffset,
    transformRange,
  } = useAppStore(
    useShallow((s) => ({
      units: s.units,
      solution: s.solution,
      solutionStale: s.solutionStale,
      solutionRevision: s.solutionRevision,
      transformedPoints: s.transformedPoints,
      transformedStale: s.transformedStale,
      transformedTimestamp: s.transformedTimestamp,
      transformedFromRevision: s.transformedFromRevision,
      system1Points: s.system1Points,
      pointNumberOffset: s.pointNumberOffset,
      transformRange: s.transformRange,
    }))
  );
  const setPointNumberOffset = useAppStore((s) => s.setPointNumberOffset);
  const setTransformRange = useAppStore((s) => s.setTransformRange);
  const transformPoints = useAppStore((s) => s.transformPoints);
  const isProcessing = useProgressStore(selectIsProcessing);
  // Keeps an emptied offset field empty while the stored offset is 0.
  const [offsetDraft, setOffsetDraft] = useState<string | null>(null);

  const plan = useMemo(
    () => planTransform(system1Points, transformRange, pointNumberOffset),
    [system1Points, transformRange, pointNumberOffset]
  );

  const solved = !!solution && !solution.error;
  const hasOutput = transformedPoints.length > 0;
  const outputCurrent = hasOutput && !transformedStale && !solutionStale;
  const unitsLabel = isUnitsSpecified(units) ? getUnitsLabel(units) : null;

  const header = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-2xl space-y-1">
        <h2 className="text-base font-semibold text-fg">Export</h2>
        <p className="text-sm text-fg-muted">
          Transform Record points with the accepted solution, then download
          PNEZD coordinates.
        </p>
      </div>
      {solved && (
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={solutionStale ? 'warning' : 'neutral'} size="md">
            Solution {solutionRevision}
            {solutionStale ? ' · outdated' : ''}
          </Badge>
          {unitsLabel && (
            <Badge tone="neutral" size="md">
              {unitsLabel}
            </Badge>
          )}
        </div>
      )}
    </div>
  );

  if (!solved) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {header}
        <Card>
          <EmptyState
            icon={<Table2 />}
            title="No solution yet"
            description="Export uses the accepted solution. Solve in Adjust, then come back to transform points."
            actions={
              <Button
                variant="primary"
                leadingIcon={<ArrowLeft />}
                onClick={onGoToAdjust}
              >
                Go to Adjust
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const offsetValue =
    offsetDraft !== null &&
    (parseInt(offsetDraft, 10) || 0) === pointNumberOffset
      ? offsetDraft
      : String(pointNumberOffset);
  const sampleNames = plan.points.slice(0, SAMPLE_NAMES).map((p) => p.name);
  const transformReason = solutionStale
    ? 'Re-solve before transforming'
    : (plan.error ?? null);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      {header}

      {solutionStale && (
        <Alert
          tone="warning"
          title="The solution is outdated"
          action={
            <Button
              size="sm"
              leadingIcon={<RefreshCw />}
              loading={isProcessing}
              onClick={onResolve}
            >
              Re-solve
            </Button>
          }
        >
          Point data changed since the last solve. Re-solve to transform with
          the current inputs; you stay on this step.
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Output options</CardTitle>
          <CardDescription>
            Choose which Record points to transform and how to name them.
          </CardDescription>
        </CardHeader>
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <Field
            id="export-offset"
            label="Renumber offset"
            help="0 keeps every identifier as written, including letters and leading zeros."
          >
            <Input
              type="number"
              inputMode="numeric"
              step={1}
              mono
              value={offsetValue}
              placeholder="0"
              onChange={(event) => {
                setOffsetDraft(event.target.value);
                setPointNumberOffset(parseInt(event.target.value, 10) || 0);
              }}
            />
          </Field>
          <Field
            id="export-range-from"
            label="From point"
            help="Leave both empty for all points."
          >
            <Input
              mono
              inputMode="numeric"
              value={transformRange.from}
              placeholder="First"
              onChange={(event) =>
                setTransformRange({
                  ...transformRange,
                  from: event.target.value,
                })
              }
            />
          </Field>
          <Field
            id="export-range-to"
            label="To point"
            help="A range selects numeric identifiers only."
          >
            <Input
              mono
              inputMode="numeric"
              value={transformRange.to}
              placeholder="Last"
              onChange={(event) =>
                setTransformRange({ ...transformRange, to: event.target.value })
              }
            />
          </Field>
        </CardBody>
        <CardFooter className="justify-between gap-3">
          {plan.error ? (
            <p
              role="alert"
              className="flex min-w-0 items-start gap-1.5 text-sm text-danger-fg"
            >
              <CircleAlert
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0"
              />
              {plan.error}
            </p>
          ) : (
            <p className="min-w-0 text-sm text-fg-muted">
              <span className="font-medium text-fg">
                {plan.points.length} point{plan.points.length === 1 ? '' : 's'}{' '}
                selected
              </span>
              <span className="hidden sm:inline">
                {' '}
                ·{' '}
                <span className="font-mono text-xs">
                  {sampleNames.join(', ')}
                </span>
                {plan.points.length > SAMPLE_NAMES ? ', …' : ''}
              </span>
            </p>
          )}
          <Button
            variant={outputCurrent ? 'secondary' : 'primary'}
            leadingIcon={<Play />}
            disabled={transformReason !== null}
            disabledReason={transformReason ?? undefined}
            onClick={transformPoints}
          >
            Transform points
          </Button>
        </CardFooter>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader
          className="flex-col pb-4 sm:flex-row"
          actions={
            hasOutput ? (
              <Button
                variant={outputCurrent ? 'primary' : 'secondary'}
                leadingIcon={<Download />}
                disabled={!outputCurrent}
                disabledReason={
                  solutionStale
                    ? 'Re-solve, then transform again'
                    : 'Output is outdated; transform again'
                }
                onClick={onDownloadCsv}
              >
                Download CSV
              </Button>
            ) : undefined
          }
        >
          <CardTitle>Transformed coordinates</CardTitle>
          <CardDescription>
            {hasOutput
              ? `${transformedPoints.length} points · solution ${transformedFromRevision ?? PLACEHOLDER}${
                  transformedTimestamp
                    ? ` · ${timeFormat.format(transformedTimestamp)}`
                    : ''
                }`
              : 'Transform points to preview the output here.'}
          </CardDescription>
        </CardHeader>

        {hasOutput ? (
          <>
            {transformedStale && !solutionStale && (
              <div className="px-4 pb-4 sm:px-5">
                <Alert tone="warning" title="Output is outdated">
                  The solution or output options changed after these coordinates
                  were generated. Transform points again before downloading.
                </Alert>
              </div>
            )}
            <Table
              stickyHeader
              containerClassName={cn(
                'max-h-[65vh] border-t border-border',
                transformedStale && 'opacity-60'
              )}
              aria-label="Transformed coordinates"
            >
              <THead>
                {/* Phones show only the output columns so they fit the screen. */}
                <Tr className="hidden md:table-row">
                  <Th colSpan={3} className={cn('h-8', EDGE_START)}>
                    <GroupHeading
                      dot="bg-record"
                      label="Record"
                      detail="source"
                    />
                  </Th>
                  <Th colSpan={3} className="h-8 border-l">
                    <GroupHeading
                      dot="bg-field"
                      label="Field"
                      detail="transformed"
                    />
                  </Th>
                  <Th
                    className={cn('h-8 border-l', EDGE_END)}
                    aria-hidden="true"
                  />
                </Tr>
                <Tr>
                  <Th
                    className={cn('hidden md:top-8 md:table-cell', EDGE_START)}
                  >
                    Point
                  </Th>
                  <Th numeric className="hidden md:top-8 md:table-cell">
                    North
                  </Th>
                  <Th numeric className="hidden md:top-8 md:table-cell">
                    East
                  </Th>
                  <Th
                    className={cn(EDGE_START, 'md:top-8 md:border-l md:px-3')}
                  >
                    Point
                  </Th>
                  <Th numeric className="md:top-8">
                    North
                  </Th>
                  <Th numeric className="md:top-8">
                    East
                  </Th>
                  <Th numeric className={cn('border-l md:top-8', EDGE_END)}>
                    <span className="inline-flex items-center gap-1">
                      <InfoTooltip label="Explain radial σ">
                        Predicted position uncertainty of the transformed point:
                        control scatter plus the uncertainty of the fitted
                        transformation. About 63% confidence; multiply by 1.73
                        for 95%.
                      </InfoTooltip>
                      Radial <span className="normal-case">σ</span>
                    </span>
                  </Th>
                </Tr>
              </THead>
              <TBody>
                {transformedPoints.map((point, index) => (
                  <Tr key={`${point.newPoint}-${index}`}>
                    <Td
                      mono
                      className={cn(
                        'hidden h-9 text-fg-muted md:table-cell',
                        EDGE_START
                      )}
                    >
                      {point.originalPoint}
                    </Td>
                    <Td
                      numeric
                      className="hidden h-9 text-fg-muted md:table-cell"
                    >
                      {point.n1}
                    </Td>
                    <Td
                      numeric
                      className="hidden h-9 text-fg-muted md:table-cell"
                    >
                      {point.e1}
                    </Td>
                    <Td
                      mono
                      className={cn(
                        EDGE_START,
                        'h-9 font-medium md:border-l md:px-3'
                      )}
                    >
                      {point.newPoint}
                    </Td>
                    <Td numeric className="h-9">
                      {point.n2}
                    </Td>
                    <Td numeric className="h-9">
                      {point.e2}
                    </Td>
                    <Td numeric className={cn('h-9 border-l', EDGE_END)}>
                      {point.stdDev || PLACEHOLDER}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <CardFooter className="justify-start text-xs text-fg-muted">
              CSV rows: Point, North, East, Elevation (0.000), Description. No
              header row; four decimals.
            </CardFooter>
          </>
        ) : (
          <CardBody className="pt-0">
            <EmptyState
              variant="dashed"
              icon={<Table2 />}
              title="No coordinates yet"
              description={
                plan.error
                  ? 'Fix the output options, then transform points.'
                  : `Transform points to generate ${plan.points.length} coordinates.`
              }
              className="py-8"
            />
          </CardBody>
        )}
      </Card>
    </div>
  );
};
